import React, { useEffect, useState, useRef } from "react";
import "../../../App.css";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useGoogleMaps } from "./useGoogleMaps";
import {
  CheckinApi,
  fetchDashboardData,
} from "../../actioncreators/actioncreators";
import TrackedHoursCard from "./Chart";
import ArthaMultiSelect from "../../fields/ArthaMultiSelect";

const Dashboard = () => {
  const { isLoaded: isMapsLoaded } = useGoogleMaps();

  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;
  const [trackedHoursData, setTrackedHoursData] = useState([
    { day: "Mon", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Tue", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Wed", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Thu", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Fri", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Sat", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Sun", Worked: 0, Breaks: 0, Overtime: 0 },
  ]);
  const [mapCenter, setMapCenter] = useState(null);
  const [totalWorkedHours, setTotalWorkedHours] = useState(null);
  const [totalBreakHours, setTotalBreakHours] = useState(null);
  const [totalOvertimeHours, setTotalOvertimeHours] = useState(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [selectedStatus, setSelectedStatus] = useState("IN"); // Changed default to "IN"
  const [members, setMembers] = useState([]);
  const [inCount, setInCount] = useState(0);
  const [outCount, setOutCount] = useState(0);
  const [response, setResponse] = useState("");
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  console.log("dashbordlocations", locations);
  useEffect(() => {
    fetchCount();
    if (mapRef.current && mapCenter) {
      const { AdvancedMarkerElement } = window.google.maps.marker;
      if (!markerRef.current) {
        markerRef.current = new AdvancedMarkerElement({
          position: mapCenter,
          map: mapRef.current,
          title: "My Advanced Marker",
        });
      } else {
        markerRef.current.position = mapCenter;
      }
    }
  }, [mapCenter]);

  // Refresh check-in/check-out counts when the Super Admin switches org/sub-org.
  useEffect(() => {
    fetchCount();
  }, [superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  const fetchMembersData = async (status) => {
    try {
      setSelectedStatus(status);
      // Send all locationIds if none selected, otherwise send selected ones
      const locationIdsToSend = selectedLocationIds.length > 0 
        ? selectedLocationIds 
        : locations.map(loc => loc.location_id);
      
      const response = await CheckinApi(status, locationIdsToSend, superAdminSelectedOrgId, superAdminSelectedSubOrgId);
      
      // Show all data from response
      const allMembers = response.data.usersData || [];
      
      setMembers(allMembers);
      if (status === "IN") {
        setInCount(allMembers.length);
      } else {
        setOutCount(allMembers.length);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const fetchCount = async () => {
    try {
      const locationIdsToSend = selectedLocationIds.length > 0
        ? selectedLocationIds
        : locations.map(loc => loc.location_id);

      const [inResponse, outResponse] = await Promise.all([
        CheckinApi("CHECKIN", locationIdsToSend, superAdminSelectedOrgId, superAdminSelectedSubOrgId),
        CheckinApi("CHECKOUT", locationIdsToSend, superAdminSelectedOrgId, superAdminSelectedSubOrgId),
      ]);

      const inMembers = inResponse.data.usersData || [];
      const outMembers = outResponse.data.usersData || [];

      setInCount(inResponse.data.totalRecords ?? inMembers.length);
      setOutCount(outResponse.data.totalRecords ?? outMembers.length);
      setMembers(inMembers);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const fetchDashboardData1 = async () => {
    try {
      // Super Admin/Admin drilled into an org or sub-org via the header's
      // Global Filter: scope the dashboard's data accordingly (see
      // actioncreator/FetchDashboardData - org_id is only honored server-side
      // for Super Admin, same as CheckinApi below, so sending it unconditionally
      // here is safe and matches the existing pattern in this file).
      const response = await fetchDashboardData(superAdminSelectedSubOrgId, superAdminSelectedOrgId);
      setResponse(response?.data?.dashboardData);
      const trackingData = response?.data?.dashboardData?.trackingData;

      // Update trackedHoursData with the workedTime data from trackingData
      if (Array.isArray(trackingData)) {
        const updatedTrackedHoursData = trackedHoursData.map((entry, index) => {
          const workData = trackingData[index];
          return workData
            ? {
                ...entry,
                Worked: convertWorkedTimeToHours(workData?.workedTime),
                Breaks: convertWorkedTimeToHours(workData?.breakTime),
                Overtime: convertWorkedTimeToHours(workData?.overTime),
              }
            : entry;
        });
        setTrackedHoursData(updatedTrackedHoursData);
      }

      setTotalWorkedHours(response?.data?.dashboardData?.totalWorkedTime);
      setTotalBreakHours(response?.data?.dashboardData?.totalBreakTime);
      setTotalOvertimeHours(response?.data?.dashboardData?.totalOverTime);

      const locationData = response?.data?.dashboardData?.location;
      if (Array.isArray(locationData)) {
        setLocations(locationData);
        if (locationData.length > 0) {
          const { latitude, longitude } = locationData[0];
          setMapCenter({ lat: latitude, lng: longitude });
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  // Helper function to convert "4h 0m" format to decimal hours
  const convertWorkedTimeToHours = (workedTime) => {
    if (!workedTime) return 0;
    const match = workedTime.match(/\d+/g);
    if (!match || match.length < 2) return 0;
    const [hours, minutes] = match.map(Number);
    return hours + minutes / 60;
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredMembers = members?.filter(
    (member) =>
      member?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.employee_id?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  useEffect(() => {
    fetchDashboardData1();
    fetchMembersData("IN"); // Add this line to fetch IN members immediately

    // Remove or comment out the fetchInitialData function call since we're explicitly handling IN status
    // const fetchInitialData = async () => { ... }
    // fetchInitialData();
  }, [superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  // Refetch data when location filter changes
  useEffect(() => {
    if (locations.length > 0) {
      fetchMembersData(selectedStatus);
    }
  }, [selectedLocationIds]);

  const formatDate = (dateString) => {
    if (!dateString) return { date: "N/A", time: "N/A" }; // Return 'N/A' if no date is available

    const date = new Date(dateString); // Parse the date string

    // Format the date as 'Mon, Nov 11, 2024'
    const formattedDate = date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "Asia/Kolkata", // Set time zone to IST (Indian Standard Time)
    });

    // Format the time as '2:30 PM' in IST

    return { date: formattedDate };
  };

  const UserAvatar = ({ member }) => {
    const [imageError, setImageError] = useState(false);

    const getInitials = (firstName, lastName) => {
      return firstName ? firstName[0].toUpperCase() : "";
    };

    // If there's no image or if there was an error loading it, show initials
    if (!member.user_image || imageError) {
      return (
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
          {getInitials(member.FirstName, member.LastName)}
        </div>
      );
    }

    // Try to render the image
    return (
      <img
        src={`data:image/jpeg;base64,${member.user_image}`}
        alt={`${member.FirstName} ${member.LastName}`}
        className="w-8 h-8 rounded-full object-cover"
        onError={() => setImageError(true)}
      />
    );
  };

  return (
    <div className="p-0 m-0 dashboard-container" style={{ fontSize: '12px' }}>
      <main className="row m-0" style={{ backgroundColor: "#EFF0F4" }}>
        <div className="col-12 border-end text-black-50 border-bottom p-2 p-md-3">
          {/* Top-right dropdowns */}

          <div className="py-2 py-md-3 text-black-50 justify-content-between gap-2 gap-md-3">
            {/* Rest of the content */}
            <div>
              <TrackedHoursCard
                className="col-12"
                trackedHoursData={trackedHoursData}
                totalWorkedHours={totalWorkedHours}
                totalBreakHours={totalBreakHours}
                totalOvertimeHours={totalOvertimeHours}
                locations={locations}
                globalLocationFilter={globalLocationFilter}
                superAdminSelectedOrgId={superAdminSelectedOrgId}
                superAdminSelectedSubOrgId={superAdminSelectedSubOrgId}
              />
            </div>

            <div className="col-12 card bg-white rounded-lg shadow p-2 p-md-3 mb-3">
              <div className="row m-0">
                <div className="col-12 items-center p-0">
                  <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 mb-2">
                    <span className="text-sm fw-bold text-gray-800">
                      Who's Checked In / Checked Out
                    </span>
                    <div className="ms-md-auto w-100 w-md-auto" style={{ maxWidth: '300px' }}>
                      <input
                        type="text"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-1000 focus:outline-none focus:border-gray-300 focus:ring-0 text-sm"
                        placeholder="Search Member..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  </div>
                  {/* <span className="text-sm text-gray-1000 italic">
                    Total {inCount + outCount} members
                  </span> */}

                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <span
                      onClick={() => fetchMembersData("IN")}
                      className={`cursor-pointer py-1 px-3 px-md-4 rounded-lg text-sm font-medium transition-colors ${
                        selectedStatus === "IN"
                          ? "bg-gray-200 text-gray-800 border-b-2 border-gray-800"
                          : "text-green-1000 hover:bg-gray-100"
                      }`}
                    >
                      Checked In
                    </span>
                    <span
                      onClick={() => fetchMembersData("OUT")}
                      className={`cursor-pointer py-1 px-3 px-md-4 rounded-lg text-sm font-medium transition-colors ${
                        selectedStatus === "OUT"
                          ? "bg-gray-200 text-gray-800 border-b-2 border-gray-800"
                          : "text-red-1000 hover:bg-gray-100"
                      }`}
                    >
                      Checked Out
                    </span>
                  </div>

                  <div className="mt-3 mt-md-4 table-responsive-wrapper">
                    {filteredMembers?.length > 0 ? (
                      <div className="table-responsive">
                        <table className="min-w-full table-auto border-collapse text-sm text-gray-700 dashboard-table">
                          <thead className="bg-gray-100 border-bottom border-top">
                            <tr>
                              <th className="px-2 px-md-4 py-1 text-left font-medium text-black fw-bold">
                                Name
                              </th>
                              <th className="px-2 px-md-4 py-1 text-left font-medium text-black fw-bold d-none d-md-table-cell">
                                Email
                              </th>
                              <th className="px-2 px-md-4 py-1 text-left font-medium text-black fw-bold d-none d-lg-table-cell">
                                Role
                              </th>
                              <th className="px-2 px-md-4 py-1 text-left font-medium text-black fw-bold">
                                Status
                              </th>
                              <th className="px-2 px-md-4 py-1 text-left font-medium text-black fw-bold d-none d-sm-table-cell">
                                Date
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredMembers?.map((member, index) => {
                              const { date } = formatDate(
                                member?.updated_date_and_time
                              );

                              return (
                                <tr
                                  key={member?.email}
                                  className={`border-b border-gray-300 transition-all ${
                                    index === members.length - 1 ? "" : "mb-2"
                                  }`}
                                >
                                  <td className="px-2 px-md-4 py-1">
                                    <div className="d-flex align-items-center gap-2">
                                      <UserAvatar
                                        member={member}
                                        className="text-start"
                                      />
                                      <span className="text-truncate" style={{ maxWidth: '120px' }}>
                                        {member?.full_name || "N/A"}
                                      </span>
                                    </div>
                                  </td>

                                  <td className="px-2 px-md-4 py-1 d-none d-md-table-cell">
                                    <span className="text-truncate d-inline-block" style={{ maxWidth: '180px' }}>
                                      {member?.email}
                                    </span>
                                  </td>
                                  <td className="px-2 px-md-4 py-1 d-none d-lg-table-cell">
                                    {member?.role_name}
                                  </td>
                                  <td
                                    className="px-2 px-md-4 py-1"
                                    style={{
                                      color:
                                        member?.current_status === "CHECKIN"
                                          ? "green"
                                          : "red",
                                    }}
                                  >
                                    {member?.current_status}
                                  </td>
                                  <td className="px-2 px-md-4 py-1 text-sm text-gray-1000 d-none d-sm-table-cell">
                                    {date}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center text-gray-1000 py-4">
                        No members found.
                      </p>
                    )}
                  </div>

                  {/* Table and other content */}
                  <div>
                    {(searchTerm ? filteredMembers : members)?.map((member) => (
                      <div key={member.id}>{member.name}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 card bg-white rounded-lg shadow p-2 p-md-3">
              <p className="fw-bold mb-2">Location</p>
              {mapCenter ? (
                <>
                  <style>
                    {`
                      .gm-style button, .gmnoprint {
                        transform: scale(0.8);
                        transform-origin: top left;
                      }
                      .gm-style button div {
                        padding: 4px 6px;
                      }
                      @media (max-width: 767px) {
                        .gm-style button, .gmnoprint {
                          transform: scale(0.7);
                        }
                      }
                    `}
                  </style>
                  {isMapsLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{
                        height: window.innerWidth < 768 ? "200px" : "300px",
                        width: "100%",
                        minHeight: "200px"
                      }}
                      center={mapCenter}
                      zoom={10}
                    >
                      {locations?.map((location, index) => (
                        <Marker
                          key={index}
                          position={{
                            lat: location.latitude,
                            lng: location.longitude,
                          }}
                        />
                      ))}
                    </GoogleMap>
                  ) : (
                    <div style={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
                      Loading map…
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center py-4">Loading map...</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
